import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { logoutAdmin } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useWindowSize } from '../../hooks/useWindowSize';
import logo from '../../assets/logo_just_salento.png';

const NAV_ITEMS = [
  {
    section: 'Principale',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: 'grid' },
      { to: '/bookings', label: 'Prenotazioni', icon: 'calendar' },
      { to: '/ordini-lido', label: 'Ordini live', icon: 'tag' },
      { to: '/beach', label: 'Mappa Spiaggia', icon: 'beach' },
    ],
  },
  {
    section: 'Configurazione',
    items: [
      { to: '/beach-builder', label: 'Beach Builder', icon: 'builder' },
      { to: '/tariffe', label: 'Tariffe', icon: 'tag' },
      { to: '/servizi', label: 'Servizi', icon: 'services' },
      { to: '/promozioni', label: 'Promozioni', icon: 'percent_discount' },
      { to: '/eventi', label: 'Eventi', icon: 'events' },
      { to: '/menu', label: 'Menu', icon: 'services' },
      { to: '/spiagge-it', label: 'Spiagge.it', icon: 'spiagge' },
      { to: '/settings', label: 'Impostazioni', icon: 'settings' },
    ],
  },
];

// Contenuto interno della sidebar (riusato sia in desktop/tablet che nel drawer mobile)
function SidebarInner({ beach, onLogout, showLabels, onNavClick }) {
  return (
    <>
      {/* Header logo */}
      <div style={{
        padding: showLabels ? '24px 20px 20px' : '14px 10px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center',
        justifyContent: showLabels ? 'flex-start' : 'center',
        gap: '10px', overflow: 'hidden',
      }}>
        <div style={{ flexShrink: 0 }}><Logo /></div>
        {showLabels && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: '16px' }}>Just Salento</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' }}>
              Admin Panel
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ padding: showLabels ? '16px 12px' : '12px 6px', flex: 1, overflowY: 'auto' }}>
        {NAV_ITEMS.map((group) => (
          <div key={group.section} style={{ marginBottom: '8px' }}>
            {showLabels && (
              <div style={{
                fontSize: '10px', color: 'var(--text-muted)',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                padding: '0 8px', marginBottom: '4px', marginTop: '12px',
              }}>
                {group.section}
              </div>
            )}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavClick}
                title={!showLabels ? item.label : undefined}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center',
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  gap: showLabels ? '10px' : '0',
                  padding: showLabels ? '9px 10px' : '10px',
                  minHeight: '44px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '13.5px',
                  color: isActive ? '#fff' : 'var(--text-soft)',
                  background: isActive ? 'var(--ocean)' : 'transparent',
                  marginBottom: '2px', transition: 'all 0.15s',
                  border: `1px solid ${isActive ? 'rgba(31,147,224,0.3)' : 'transparent'}`,
                  textDecoration: 'none',
                })}
              >
                <span style={{ width: '18px', height: '18px', flexShrink: 0, opacity: 0.85 }}>
                  <Icon name={item.icon} />
                </span>
                {showLabels && item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: showLabels ? '12px' : '8px', borderTop: '1px solid var(--border)' }}>
        {showLabels && beach && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: '8px',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
              Stabilimento
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500 }}>{beach.nome}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {beach.indirizzo?.split(',')[0]}
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          style={{
            width: '100%', minHeight: '44px',
            padding: showLabels ? '8px' : '10px',
            borderRadius: 'var(--radius-md)',
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--text-muted)', fontSize: '12.5px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: showLabels ? '6px' : '0',
            fontFamily: 'var(--font-sans)', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-soft)'; e.currentTarget.style.borderColor = 'var(--border-mid)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <LogoutIcon />{showLabels && ' Esci'}
        </button>
      </div>
    </>
  );
}

export default function Sidebar() {
  const { beach } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useWindowSize();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tabletExpanded, setTabletExpanded] = useState(false);

  const handleLogout = async () => {
    await logoutAdmin();
    navigate('/login');
  };

  // ── Mobile: hamburger fisso + drawer overlay ──────────────────────────────
  if (isMobile) {
    return (
      <>
        <button
          aria-label="Apri menu"
          onClick={() => setDrawerOpen(true)}
          style={{
            position: 'fixed', top: '14px', left: '16px', zIndex: 201,
            background: 'var(--navy-mid)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            width: '40px', height: '40px',
            cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-soft)',
          }}
        >
          <HamburgerIcon />
        </button>

        {drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
            }}
          >
            <aside
              onClick={e => e.stopPropagation()}
              style={{
                width: '260px', height: '100%',
                background: 'var(--navy-mid)',
                borderRight: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column',
                boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
              }}
            >
              <SidebarInner
                beach={beach}
                onLogout={handleLogout}
                showLabels
                onNavClick={() => setDrawerOpen(false)}
              />
            </aside>
          </div>
        )}
      </>
    );
  }

  // ── Tablet: sidebar collassabile (solo icone, espande al hover) ───────────
  // ── Desktop: sidebar fissa 220px ─────────────────────────────────────────
  const collapsed = isTablet && !tabletExpanded;

  return (
    <aside
      style={{
        width: collapsed ? '56px' : '220px',
        background: 'var(--navy-mid)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={() => isTablet && setTabletExpanded(true)}
      onMouseLeave={() => isTablet && setTabletExpanded(false)}
    >
      <SidebarInner
        beach={beach}
        onLogout={handleLogout}
        showLabels={!collapsed}
      />
    </aside>
  );
}

function Logo() {
  return (
    <img src={logo} alt="Just Salento" style={{ width: '36px', height: '36px' }} />
  );
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 18 14" fill="none" width="18" height="14">
      <line x1="0" y1="1" x2="18" y2="1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="0" y1="7" x2="18" y2="7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="0" y1="13" x2="18" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function Icon({ name }) {
  const icons = {
    grid: <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16"><rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" /><rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" /></svg>,
    calendar: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><rect x="2" y="3" width="12" height="11" rx="1.5" /><line x1="5" y1="1" x2="5" y2="5" strokeLinecap="round" /><line x1="11" y1="1" x2="11" y2="5" strokeLinecap="round" /><line x1="2" y1="7" x2="14" y2="7" /></svg>,
    beach: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><circle cx="8" cy="6" r="3" /><path d="M2 13 Q8 9 14 13" strokeLinecap="round" /></svg>,
    builder: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" /><rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" strokeDasharray="2 1" /></svg>,
    tag: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><path d="M2 2h6l6 6-6 6-6-6V2z" strokeLinejoin="round" /><circle cx="6" cy="6" r="1" fill="currentColor" stroke="none" /></svg>,
    services: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><path d="M6 4h8M6 8h8M6 12h5" strokeLinecap="round" /><circle cx="2.5" cy="4" r="1" fill="currentColor" stroke="none" /><circle cx="2.5" cy="8" r="1" fill="currentColor" stroke="none" /><circle cx="2.5" cy="12" r="1" fill="currentColor" stroke="none" /></svg>,
    settings: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><circle cx="8" cy="8" r="2.5" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" strokeLinecap="round" /></svg>,
    spiagge: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><circle cx="8" cy="6" r="2.5" /><path d="M2 14 Q5 10 8 10 Q11 10 14 14" strokeLinecap="round" /><path d="M8 1 L8 3.5" strokeLinecap="round" /><path d="M5 2 Q6.5 3 8 3.5 Q9.5 3 11 2" strokeLinecap="round" /></svg>,
    events: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16"><rect x="2" y="3" width="12" height="11" rx="1.5" /><line x1="5" y1="1" x2="5" y2="5" strokeLinecap="round" /><line x1="11" y1="1" x2="11" y2="5" strokeLinecap="round" /><line x1="2" y1="7" x2="14" y2="7" /><line x1="5" y1="10" x2="11" y2="10" strokeLinecap="round" /><line x1="5" y1="13" x2="8" y2="13" strokeLinecap="round" /></svg>,
    percent_discount: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="16" height="16">
      <circle cx="5" cy="5" r="1.5" />
      <circle cx="11" cy="11" r="1.5" />
      <line x1="13" y1="3" x2="3" y2="13" strokeLinecap="round" />
    </svg>,
  };
  return icons[name] || null;
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="14" height="14">
      <path d="M6 3H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3" strokeLinecap="round" />
      <path d="M10 5l3 3-3 3M13 8H6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}